import { useState, useEffect } from "react";
import { Edit2, Trash2, Image as ImageIcon, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

/**
 * Tarjeta de producto con edición inline directa (sin abrir el lápiz).
 * Permite editar nombre, precio, descripción e imagen desde la propia lista.
 * El botón del lápiz queda reservado para Alérgenos y Opciones.
 */
export default function InlineProductCard({
  product,
  categoryId,
  onSaveFields,
  onImageChange,
  onToggle,
  onEdit,
  onDelete,
  uploading,
}) {
  const [name, setName] = useState(product.name);
  const [price, setPrice] = useState(product.price);
  const [description, setDescription] = useState(product.description || "");

  // Re-sincronizar cuando el producto cambia externamente (tras guardar)
  useEffect(() => {
    setName(product.name);
    setPrice(product.price);
    setDescription(product.description || "");
  }, [product.id, product.name, product.price, product.description]);

  const isInactive = product.active === false;

  const commitName = () => {
    const trimmed = (name || "").trim();
    if (!trimmed) {
      setName(product.name); // revertir si queda vacío
      return;
    }
    if (trimmed !== product.name) {
      onSaveFields(product, categoryId, { name: trimmed });
    }
  };

  const commitPrice = () => {
    const parsed = parseFloat(price);
    if (isNaN(parsed) || parsed < 0) {
      setPrice(product.price); // revertir si es inválido
      return;
    }
    if (parsed !== product.price) {
      onSaveFields(product, categoryId, { price: parsed });
    }
  };

  const commitDescription = () => {
    if ((description || "") !== (product.description || "")) {
      onSaveFields(product, categoryId, { description });
    }
  };

  const triggerImagePicker = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.onchange = (e) => onImageChange(e.target.files[0], product, categoryId);
    input.click();
  };

  return (
    <div
      className={`p-4 border rounded-lg ${isInactive ? "bg-gray-50" : "bg-white hover:bg-gray-50"} transition`}
      data-testid={`product-card-${product.id}`}
    >
      <div className="flex gap-4 items-start">
        {/* Imagen editable */}
        <div className="relative flex-shrink-0 group/img">
          {product.image_url ? (
            <img
              src={product.image_url}
              alt={product.name}
              className="w-20 h-20 object-cover rounded-lg"
            />
          ) : (
            <div className="w-20 h-20 rounded-lg bg-gray-100 flex items-center justify-center">
              <ImageIcon className="w-6 h-6 text-gray-400" />
            </div>
          )}
          <button
            type="button"
            onClick={triggerImagePicker}
            disabled={uploading}
            data-testid={`inline-image-btn-${product.id}`}
            className="absolute inset-0 bg-black/50 opacity-0 group-hover/img:opacity-100 transition rounded-lg flex items-center justify-center text-white text-[11px] font-medium"
            title="Cambiar imagen"
          >
            {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Cambiar"}
          </button>
        </div>

        {/* Campos editables */}
        <div className="flex-1 min-w-0 space-y-2">
          <Input
            data-testid={`inline-name-${product.id}`}
            value={name}
            onChange={(e) => setName(e.target.value)}
            onBlur={commitName}
            onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
            className={`h-9 font-semibold ${isInactive ? "text-gray-400" : "text-gray-900"}`}
            placeholder="Nombre del producto"
          />
          <div className="flex items-center gap-3 flex-wrap">
            <div className="relative w-28">
              <Input
                data-testid={`inline-price-${product.id}`}
                type="number"
                step="0.01"
                min="0"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                onBlur={commitPrice}
                onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
                className="h-9 pr-6 font-bold text-emerald-600"
              />
              <span className="absolute right-2 top-1/2 -translate-y-1/2 text-emerald-600 font-bold pointer-events-none">
                €
              </span>
            </div>
            {isInactive && (
              <span className="text-xs bg-gray-200 text-gray-600 px-2 py-1 rounded">Inactivo</span>
            )}
          </div>
          <Input
            data-testid={`inline-desc-${product.id}`}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            onBlur={commitDescription}
            onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
            className="h-9 text-sm text-gray-600"
            placeholder="Añadir descripción..."
          />
          {product.options && product.options.length > 0 && (
            <div className="flex gap-1 flex-wrap">
              {product.options.map((opt) => (
                <span
                  key={opt}
                  className="text-xs bg-emerald-100 text-emerald-700 px-2 py-1 rounded"
                >
                  {opt}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Acciones */}
        <div className="flex gap-2 flex-shrink-0">
          <Button
            size="sm"
            variant="ghost"
            onClick={() => onToggle(product.id, categoryId)}
            className={isInactive ? "text-green-600 hover:text-green-700 hover:bg-green-50" : "text-gray-600 hover:text-gray-700 hover:bg-gray-50"}
            title={isInactive ? "Activar producto" : "Desactivar producto"}
          >
            {isInactive ? "Activar" : "Desactivar"}
          </Button>
          <Button
            data-testid={`edit-product-btn-${product.id}`}
            size="sm"
            variant="ghost"
            onClick={() => onEdit(product)}
            className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
            title="Alérgenos y opciones"
          >
            <Edit2 className="w-4 h-4" />
          </Button>
          <Button
            data-testid={`delete-product-btn-${product.id}`}
            size="sm"
            variant="ghost"
            onClick={() => onDelete(product.id, categoryId)}
            className="text-red-500 hover:text-red-700 hover:bg-red-50"
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
